
import { Component, Inject, Prop } from "vue-property-decorator";
import VueI18n from "vue-i18n";
import { CanadidateReportInfo, TypedVue } from "@/store/types";
import { ProjectTheme } from "@/store/admin/adminTypes";
import { Trait, FOUR_GROUP_TRAIT_GROUPINGS, TRAIT_CLASS_RANGES } from "@/lib/consts";
import { ComparisonAssessmentItem, ComparisonBenchmarkItem } from "@/components/admin/ComparisonComponents/ComparisonRadarChartComponent.vue";
import { getOrientationText, getTraitContent, getTraitDevSuggestions } from "@/lib/elraReport7DynamicText";
import ElraReport8Hero from "./ElraReport8/ElraReport8Hero.vue";
import ElraReport8Orientations from "./ElraReport8/ElraReport8Orientations.vue";
import ElraReport8FullPicture from "./ElraReport8/ElraReport8FullPicture.vue";
import ElraReport8DevNextSteps from "./ElraReport8/ElraReport8DevNextSteps.vue";
import ElraReport8Assistant from "./ElraReport8/ElraReport8Assistant.vue";
import { TraitInfoV8, OrientationGroupV8 } from "./ElraReport8/types";

// Note: "poeple-orientation" key matches the existing typo in FOUR_GROUP_TRAIT_GROUPINGS.
// Label and description for each orientation live in the report:elra v8 literals doc as
// Report::v8::Orientation::{id}::label and ::description.
const ORIENTATION_META_V8: Record<string, { letter: string }> = {
  "strategic-orientation": { letter: "S" },
  "poeple-orientation": { letter: "P" },
  "outcome-orientation": { letter: "O" },
  "communication-orientation": { letter: "C" },
};

@Component({
  components: { ElraReport8Hero, ElraReport8Orientations, ElraReport8FullPicture, ElraReport8DevNextSteps, ElraReport8Assistant },
})
export default class ElraReport8 extends TypedVue {
  // Shadows the global $i18n with the per-report instance provided by CandidateReport.vue,
  // so $t() lookups in this subtree resolve against admin literals merged with report:elra v8.
  @Inject("$i18n") readonly $i18n!: VueI18n;

  @Prop({ required: true }) public data!: CanadidateReportInfo;
  @Prop({ required: true }) private showPII!: boolean;
  @Prop() traitClassRanges!: Record<Trait, { low: number; high: number; lowerlow: number; mean: number; standardDeviation: number }>;
  @Prop({ default: ProjectTheme.DEFAULT_TA }) theme!: string;
  @Prop({ default: () => [] }) radarCandidates!: ComparisonAssessmentItem[];
  @Prop({ default: () => [] }) radarBenchmarks!: ComparisonBenchmarkItem[];
  @Prop({ default: false }) radarShowNames!: boolean;
  @Prop({ default: false }) radarAllowModeSwitch!: boolean;
  @Prop({ default: "competency" }) radarMode!: string;
  @Prop({ default: "" }) radarColorUniqueKey!: string;
  @Prop({ default: false }) radarFourGroup!: boolean;
  @Prop({ default: false }) showAiInsights!: boolean; // v8 has no AI insights section; prop accepted to match parent call site

  private assistantOpen = false;

  get candidateDisplayName(): string {
    return this.showPII ? this.data.data.candidate_name : this.data.data.assessmentId.substring(0, 4).toUpperCase();
  }

  get readiness(): string {
    return this.data.readiness || "default";
  }

  get readinessLabel(): string {
    return this.$t("Report::Readiness::text::" + this.readiness).toString();
  }

  get heroDescription(): string {
    return this.$t("Report::v5::YourLeadershipProfile::" + this.readiness + "::sidetext").toString();
  }

  // Hero eyebrow: the assessment completion date, at day precision. Falls back to the
  // report's creation date for older reports whose payload predates date_completed.
  get assessmentDate(): string {
    const raw = this.data.data.date_completed ?? this.data.data.date_created;
    if (!raw) return "";
    return new Date(raw).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  get footerDate(): string {
    const raw = this.data.data.date_created;
    if (!raw) return "";
    return new Date(raw).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }

  get orientationGroups(): OrientationGroupV8[] {
    return Object.entries(FOUR_GROUP_TRAIT_GROUPINGS).map(([orientationId, traits]) => {
      const meta = ORIENTATION_META_V8[orientationId];
      const scoring = this.data.data.scoring ?? {};
      const orientationLabel = this.$t(`Report::v8::Orientation::${orientationId}::label`).toString();
      const orientationDescription = this.$t(`Report::v8::Orientation::${orientationId}::description`).toString();

      const traitInfos: TraitInfoV8[] = (traits as Trait[]).map((trait) => {
        const traitScore: number = this.data.data.scoring?.[`${trait}_bellcurve`] ?? 0;
        const cutoffs = this.traitClassRanges?.[trait] ?? TRAIT_CLASS_RANGES[trait];
        const priority: "high" | "mixed" | "low" = traitScore >= cutoffs.high ? "high" : traitScore >= cutoffs.low ? "mixed" : "low";
        const displayName = this.$t(`Comparison::TraitChart::XAxisLabel::${trait}`).toString();
        const { definition, behaviours } = getTraitContent(trait, this.$i18n);
        const devSuggestions = getTraitDevSuggestions(trait, this.$i18n);
        return { key: trait, displayName, priority, score: traitScore, benchLow: cutoffs.low, benchHigh: cutoffs.high, definition, behaviours, devSuggestions, orientationLabel };
      });

      const highlyPrioritizedTraits = traitInfos.filter((t) => t.priority === "high");
      const prioritizedTraits = traitInfos.filter((t) => t.priority === "mixed");
      const lessPrioritizedTraits = traitInfos.filter((t) => t.priority === "low");
      const prioritizedCount = traitInfos.filter((t) => t.priority !== "low").length;
      const totalCount = traitInfos.length;

      return {
        id: orientationId,
        letter: meta?.letter ?? "",
        label: orientationLabel,
        description: orientationDescription,
        traits: traitInfos,
        highlyPrioritizedTraits,
        prioritizedTraits,
        lessPrioritizedTraits,
        prioritizedCount,
        totalCount,
        orientationText: getOrientationText(orientationId, scoring, this.$i18n),
      };
    });
  }

  get heroRadarTraitScores(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const og of this.orientationGroups) {
      for (const t of og.traits) out[t.key] = t.score;
    }
    return out;
  }

  get heroRadarTraitPriorities(): Record<string, "high" | "mixed" | "low"> {
    const out: Record<string, "high" | "mixed" | "low"> = {};
    for (const og of this.orientationGroups) {
      for (const t of og.traits) out[t.key] = t.priority;
    }
    return out;
  }

  get heroRadarColor(): string {
    return "#9bcc50";
  }

  get lessPrioritizedTraits(): TraitInfoV8[] {
    return this.orientationGroups
      .flatMap((og) => og.lessPrioritizedTraits)
      .sort((a, b) => a.score - b.score)
      .slice(0, 3);
  }

  get prioritizedCount(): number {
    return this.orientationGroups.reduce((sum, og) => sum + og.prioritizedCount, 0);
  }

  get totalCount(): number {
    return 15;
  }

  get orientationStats(): Array<{ letter: string; label: string; prioritizedCount: number; totalCount: number }> {
    return this.orientationGroups.map((og) => ({
      letter: og.letter,
      label: og.label,
      prioritizedCount: og.prioritizedCount,
      totalCount: og.totalCount,
    }));
  }

  formatSentence(text: string): string {
    if (!text) return text;
    const result = text.charAt(0).toUpperCase() + text.slice(1);
    return result.endsWith(".") ? result : result + ".";
  }

  mounted() {
    this.$nextTick(() => {
      window.dispatchEvent(
        new CustomEvent("report-template-updated", {
          detail: { container: this.$el },
        }),
      );
    });
    this.$matomo && this.$matomo.trackEvent("Report", "Loaded", this.data.version, null, { shortId: this.data.id, projectId: this.data.projectId, version: this.data.version });
  }
}
